
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";

export const useFilteredRoutines = (planId: string) => {
  const { profile } = useProfile();

  return useQuery({
    queryKey: ["filtered-routines", planId, profile?.id],
    queryFn: async () => {
      if (!profile) return [];
      
      try {
        // 1. Obtener las rutinas que ya están en el plan actual
        const { data: planRoutineLinks, error: planError } = await supabase
          .from("workout_plan_routines")
          .select("routine_id")
          .eq("workout_plan_id", planId);
          
        if (planError) throw planError;
        
        const existingRoutineIds = planRoutineLinks?.map(link => link.routine_id) || [];
        
        // 2. Obtener los planes a los que el usuario está suscrito
        const { data: subscriptions, error: subsError } = await supabase
          .from("workout_plan_subscriptions")
          .select("workout_plan_id")
          .eq("user_id", profile.id);
          
        if (subsError) throw subsError;
        
        const subscribedPlanIds = subscriptions?.map(sub => sub.workout_plan_id) || [];
        
        // 3. Obtener rutinas de los planes suscritos
        let subscribedRoutineIds: string[] = [];
        
        if (subscribedPlanIds.length > 0) {
          const { data: subscribedRoutineLinks, error: subRoutinesError } = await supabase
            .from("workout_plan_routines")
            .select("routine_id")
            .in("workout_plan_id", subscribedPlanIds);
            
          if (subRoutinesError) throw subRoutinesError;
          
          subscribedRoutineIds = subscribedRoutineLinks?.map(link => link.routine_id) || [];
        }
        
        // 4. Obtener todas las rutinas del usuario (propias o copiadas)
        const { data: userRoutines, error: routinesError } = await supabase
          .from("routines")
          .select("*")
          .eq("user_id", profile.id);
          
        if (routinesError) throw routinesError;
        
        // 5. Filtrar rutinas:
        // - Incluir TODAS las rutinas del usuario (creadas o copiadas) que no estén ya en el plan actual
        // - Los profesores no necesitan copiar sus propias rutinas para usarlas
        const filteredRoutines = userRoutines?.filter(routine => {
          // No incluir rutinas que ya están en el plan actual
          if (existingRoutineIds.includes(routine.id)) return false;

          // Incluir cualquier rutina propia del usuario (todas las devueltas ya son suyas)
          if (routine.user_id === profile.id) return true;

          // Incluir rutinas de planes suscritos
          if (subscribedRoutineIds.includes(routine.id)) return true;

          // Incluir rutinas copiadas (legacy: detectadas por nombre)
          if (routine.name.includes("(Copiada)")) return true;

          return false;
        });
        
        return filteredRoutines || [];
      } catch (error) {
        console.error("Error al filtrar rutinas:", error);
        throw error;
      }
    },
    enabled: !!profile && !!planId,
  });
};
